/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {defineMessage, useIntl} from 'react-intl'
import {Stack, Box, useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui'
import useProfileFields from './useProfileFields'
import Field from '../field'
import {FormattedMessage} from 'react-intl'
import {CalendarGreyIcon} from '../icons'
import {Text} from '@salesforce/retail-react-app/app/components/shared/ui'

const ProfileFields = ({form, prefix = '', birthDayData = null}) => {
    const fields = useProfileFields({form, prefix})
    const intl = useIntl()

    const isDesktop = useBreakpointValue({base: false, lg: true})
    const formTitleAriaLabel = defineMessage({
        defaultMessage: 'Profile Form',
        id: 'profile_fields.label.profile_form'
    })

    const customLabelProps = {
        labelProps: {
            color: 'grey.600 !important',
            fontSize: 'md',
            lineHeight: '24px'
        }
    }

    return (
        <Stack spacing={14} aria-label={intl.formatMessage(formTitleAriaLabel)}>
            <Stack spacing={3}>
                <Text textStyle="webH4">
                    <FormattedMessage
                        defaultMessage="Contact Information"
                        id="contact_info.title.contact_info"
                    />
                </Text>
                <Field
                    inputProps={{
                        disabled: true
                    }}
                    {...fields.email}
                />
                <Field {...fields.phone} {...customLabelProps} />
            </Stack>

            <Stack spacing={3}>
                <Text textStyle="webH4">
                    <FormattedMessage
                        defaultMessage="Personal Details"
                        id="contact_info.title.personal_details"
                    />
                </Text>
                <Field {...fields.firstName} {...customLabelProps} />
                <Field {...fields.lastName} {...customLabelProps} />
                <Box position="relative">
                    <Field
                        inputProps={{
                            min: `${new Date().getFullYear() - 100}-01-01`,
                            max: new Date().toISOString().split('T')[0],
                            pr: birthDayData ? 8 : 0,
                            onKeyDown: (e) => {
                                const allowedKeys = /^(Tab|Enter|Escape|Backspace|Delete|\d)$/
                                if (!allowedKeys.test(e.key)) {
                                    e.preventDefault()
                                }
                            },
                            onPaste: (e) => e.preventDefault(),
                            disabled: !!birthDayData
                        }}
                        {...fields.birthDay}
                        type={birthDayData ? 'text' : 'date'}
                        variant={'webFloatingField'}
                        {...customLabelProps}
                    />
                    {birthDayData && (
                        <Box
                            position="absolute"
                            right={2}
                            top="50%"
                            transform="translateY(-50%)"
                            pointerEvents="none"
                        >
                            <CalendarGreyIcon w={5} h={5} />
                        </Box>
                    )}
                </Box>
                <Text textStyle="bodyXsRegular" color="webTypo.textSecondary">
                    <FormattedMessage
                        id="contact_info.message.birthday_gift"
                        defaultMessage="Enjoy a special gift from us on your big day."
                    />
                </Text>
            </Stack>
        </Stack>
    )
}

ProfileFields.propTypes = {
    /** Object returned from `useForm` */
    form: PropTypes.object.isRequired,

    /** Optional prefix for field names */
    prefix: PropTypes.string,

    /** Data for the birthday field */
    birthDayData: PropTypes.string
}

export default ProfileFields
